/**
 * SF-4656 — "Feature Flags" tab on the company detail page.
 *
 * One place for every per-company feature switch. Each switch saves on its
 * own, straight away (see useSaveCompanyField / McpAccessSwitch), and the
 * page shows a feature's setup tab only while its switch is on:
 *
 *  - SSO integration (`sso_enabled`). Needs admin:sso:read to see,
 *    admin:sso:write + admin:companies:write to change (sf-api checks both).
 *    A company that already HAS an SSO setup counts as on and can't be
 *    switched off until the setup is deleted, so a company that signs in with
 *    SSO never loses its setup screen. If that check fails the switch is
 *    locked (the page still shows the SSO tab): we can't tell what it means.
 *  - CSAT custom domain (`csat_custom_domain_enabled`). Moved here from the
 *    CSAT Custom Domain tab (SF-4373) with the same confirmations and the same
 *    rule: admin:companies:domain:read to see, admin:companies:domain:write +
 *    admin:companies:write to change.
 *  - Help Desk (`help_desk_module_enabled`, SF-5055). The company's Help Desk
 *    module in the web app, the same switch as the web app's company
 *    permissions page (sf-api saves it there). Off hides Help Desk in the web
 *    app, and the page shows the Help Desk settings tab only while it is on.
 *    Changing it needs admin:companies:write and the company on the staff
 *    member's list. null (or missing) from sf-api = could not be read: the
 *    switch is locked, and the Help Desk tab stays shown.
 *  - External AI access (`mcp_access`). The SF-4576 McpAccessSwitch,
 *    unchanged, moved here from General Info.
 *  - Eva helpdesk replies moved to the Help Desk tab (SF-5055).
 *
 * SF-4762: a switch whose company field is linked to a platform flag that is
 * on shows as on, greyed, with PLATFORM_ON_NOTE. Display only: nothing is
 * saved, so the company's own value is back when the platform flag goes off.
 * No live flag is linked yet. If the flag list can't load, nothing greys.
 *
 * Sign-in itself does not read `sso_enabled`; each SSO setup's own on/off
 * still decides that (sf-auth-service).
 */
import { Space, Typography } from 'antd';
import { McpAccessSwitch } from './McpAccessSwitch';
import { FlagSwitch } from './FlagSwitch';
import type { PendingSave } from './useSaveCompanyField';
import type { Company } from '../list';

const { Text } = Typography;

export interface SsoSetupCheck {
  /**
   * Is SSO live (a setup that is switched on)? undefined while loading or
   * when the check failed.
   */
  live: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
}

export interface FeatureFlagsTabProps {
  company: Company;
  companyId: string;
  /** Refine companies:edit (admin:companies:write), needed by every flag here. */
  canEditCompany: boolean;
  /**
   * SF-4668: false when this company is not on the staff member's company list. Kept apart from
   * `canEditCompany` so the note gives the right reason. Defaults to true.
   */
  companyOnList?: boolean;
  /** SF-5055: the company list lookup failed, so `companyOnList` false means unknown. */
  scopeCheckFailed?: boolean;
  /** admin:sso:read — shows the SSO row. */
  canReadSso: boolean;
  /** admin:sso:write */
  canWriteSso: boolean;
  /** admin:companies:domain:read — shows the CSAT row. */
  canReadCsatDomain: boolean;
  /** admin:companies:domain:write */
  canWriteCsatDomain: boolean;
  /**
   * SF-4762: company fields a linked platform flag switches on for every
   * company. Passed by the page, which also uses it for the setup tabs, so a
   * switch that reads as on always has its tab.
   */
  platformOnFields: ReadonlySet<string>;
  permissionsLoading: boolean;
  ssoSetup: SsoSetupCheck;
  /** The page's single-field saver; one save at a time across the page. */
  saver: {
    save: <K extends keyof Company>(field: K, value: Company[K], successText: string) => Promise<boolean>;
    pending: PendingSave | null;
    busy: boolean;
  };
  onSaved: (saved: Company) => void;
  refetchCompany: () => Promise<{ isError?: boolean }>;
}

const COMPANY_WRITE_NOTE = 'Changing this also needs company edit rights (admin:companies:write).';
const NOT_ON_LIST_NOTE = 'This company is not on your list of companies, so you can only view it.';
const SCOPE_CHECK_FAILED_NOTE = 'Could not check your company edit access. Try again.';
const PLATFORM_ON_NOTE = 'On for every company (platform setting)';
const HELP_DESK_WRITE_NOTE = 'Changing this needs company edit rights (admin:companies:write).';
const HELP_DESK_UNKNOWN_NOTE =
  'Could not read the Help Desk switch from the web app. Refresh the page to try again.';

const FlagRow: React.FC<{ title: string; note: string; children: React.ReactNode; testId: string }> = ({
  title,
  note,
  children,
  testId,
}) => (
  <Space align="start" data-testid={testId} style={{ width: '100%' }}>
    {children}
    <div>
      <Text strong>{title}</Text>
      <br />
      <Text type="secondary">{note}</Text>
    </div>
  </Space>
);

export const FeatureFlagsTab: React.FC<FeatureFlagsTabProps> = ({
  company,
  companyId,
  canEditCompany,
  companyOnList = true,
  scopeCheckFailed = false,
  canReadSso,
  canWriteSso,
  canReadCsatDomain,
  canWriteCsatDomain,
  platformOnFields,
  permissionsLoading,
  ssoSetup,
  saver,
  onSaved,
  refetchCompany,
}) => {
  const pendingFor = (field: keyof Company): boolean | null =>
    saver.pending?.field === field ? (saver.pending.value as boolean) : null;
  const ssoPlatformOn = platformOnFields.has('sso_enabled');
  const csatPlatformOn = platformOnFields.has('csat_custom_domain_enabled');

  const notOnListNote = scopeCheckFailed ? SCOPE_CHECK_FAILED_NOTE : NOT_ON_LIST_NOTE;

  // ---- SSO ---------------------------------------------------------------
  const ssoSaved = company.sso_enabled === true;
  const ssoOn = ssoSaved || ssoSetup.live === true || ssoPlatformOn;
  // While permissions load, no "you can't" note: the switches are just locked
  // (no false refusal for staff who do have access; Bugbot).
  const ssoReason = ssoPlatformOn
    ? PLATFORM_ON_NOTE
    : permissionsLoading
    ? null
    : !companyOnList
    ? notOnListNote
    : !canWriteSso
    ? 'Changing this needs SSO admin rights (admin:sso:write).'
    : !canEditCompany
      ? COMPANY_WRITE_NOTE
      : ssoSetup.live === true
        ? 'SSO is live. Switch it off on the SSO Configuration tab first.'
        : ssoSetup.isError
          ? 'Could not check whether this company has an SSO setup. Refresh the page to try again.'
          : null;
  const ssoDisabled =
    permissionsLoading || ssoSetup.isLoading || saver.busy || ssoReason !== null;

  // ---- CSAT custom domain -----------------------------------------------
  const csatSaved = company.csat_custom_domain_enabled === true;
  const csatReason = csatPlatformOn
    ? PLATFORM_ON_NOTE
    : permissionsLoading
    ? null
    : !companyOnList
    ? notOnListNote
    : !canWriteCsatDomain
    ? 'You have read-only access to custom-domain setup (admin:companies:domain:write required to change it).'
    : !canEditCompany
      ? COMPANY_WRITE_NOTE
      : null;
  const csatDisabled = permissionsLoading || saver.busy || csatReason !== null;

  // ---- Help Desk (the web app's module switch, SF-5055) ------------------
  const helpDeskValue = company.help_desk_module_enabled;
  const helpDeskKnown = typeof helpDeskValue === 'boolean';
  const helpDeskReason = permissionsLoading
    ? null
    : !companyOnList
    ? notOnListNote
    : !canEditCompany
    ? HELP_DESK_WRITE_NOTE
    : !helpDeskKnown
    ? HELP_DESK_UNKNOWN_NOTE
    : null;
  const helpDeskDisabled =
    permissionsLoading || saver.busy || !helpDeskKnown || helpDeskReason !== null;

  return (
    <Space direction="vertical" size="large" data-testid="feature-flags-tab" style={{ width: '100%' }}>
      {canReadSso && (
        <FlagRow
          testId="feature-flag-sso"
          title="SSO integration"
          note={
            ssoReason ??
            'Shows the SSO Configuration tab so this company can be set up to sign in with its own identity provider. Saved immediately.'
          }
        >
          <FlagSwitch
            testId="sso-flag-toggle"
            ariaLabel="SSO integration"
            checked={ssoOn}
            pendingValue={pendingFor('sso_enabled')}
            loading={permissionsLoading || ssoSetup.isLoading}
            disabled={ssoDisabled}
            disabledReason={ssoReason}
            confirmOff={{
              title: 'Switch SSO integration off?',
              description:
                'The SSO Configuration tab is hidden. Nothing is deleted: any SSO setup is kept, switched off, for when this is turned back on.',
              okText: 'Switch off',
            }}
            onChange={(next) =>
              void saver.save(
                'sso_enabled',
                next,
                next ? 'SSO integration switched on' : 'SSO integration switched off',
              )
            }
          />
        </FlagRow>
      )}

      {canReadCsatDomain && (
        <FlagRow
          testId="feature-flag-csat-domain"
          title="CSAT custom domain"
          note={csatReason ?? 'Chargeable add-on. Shows the CSAT Custom Domain tab. Saved immediately.'}
        >
          <FlagSwitch
            testId="csat-domain-toggle"
            ariaLabel="Use custom sending domain"
            checked={csatSaved || csatPlatformOn}
            pendingValue={pendingFor('csat_custom_domain_enabled')}
            loading={permissionsLoading}
            disabled={csatDisabled}
            disabledReason={csatReason}
            confirmOn={{
              title: 'Switch the custom sending domain on?',
              description:
                'Chargeable add-on. Lets this company add verified custom domains and choose which one (or the standard ServeFirst address) its links and emails use by default.',
              okText: 'Switch on',
            }}
            confirmOff={{
              title: 'Switch the custom sending domain off?',
              description:
                'All links and emails go back to the standard ServeFirst address. Nothing is deleted — domains, DNS records and certificates are kept so it can be switched on again.',
              okText: 'Switch off',
            }}
            onChange={(next) =>
              void saver.save(
                'csat_custom_domain_enabled',
                next,
                next ? 'Custom sending domain switched on' : 'Custom sending domain switched off',
              )
            }
          />
        </FlagRow>
      )}

      <FlagRow
        testId="feature-flag-help-desk"
        title="Help Desk"
        note={
          helpDeskReason ??
          "The company's Help Desk module in the web app: the same switch as on the web app's company permissions page. Shows the Help Desk tab with this company's help desk settings. Saved immediately."
        }
      >
        <FlagSwitch
          testId="help-desk-module-toggle"
          ariaLabel="Help desk"
          checked={helpDeskValue === true}
          pendingValue={pendingFor('help_desk_module_enabled')}
          loading={permissionsLoading}
          disabled={helpDeskDisabled}
          disabledReason={helpDeskReason}
          confirmOff={{
            title: 'Switch help desk off?',
            description:
              'Help Desk is hidden in the web app for everyone at this company, and the Help Desk tab here is hidden. Tickets and settings are kept, and come back when it is switched on again.',
            okText: 'Switch off',
          }}
          onChange={(next) =>
            void saver.save(
              'help_desk_module_enabled',
              next,
              next ? 'Help desk switched on' : 'Help desk switched off',
            )
          }
        />
      </FlagRow>

      <div data-testid="feature-flag-mcp">
        <Text strong>External AI access (Claude, ChatGPT)</Text>
        <div style={{ marginTop: 4 }}>
          {/* key: the route element is reused across ids (see SF-4576). */}
          <McpAccessSwitch
            key={companyId}
            companyId={companyId}
            enabled={company.mcp_access === true}
            onSaved={onSaved}
            refetchCompany={refetchCompany}
            companyName={company.name}
          />
        </div>
      </div>
    </Space>
  );
};
